import { omitKeys } from "@hex-insights/core";
import {
	Address,
	Country,
	EmailAddress,
	Employee,
	Guardian,
	Language,
	Person,
	PersonEnglishLanguageFluency,
	PersonNameOrder,
	PersonPublicNameFormat,
	PhoneNumber,
	Student,
	User,
} from "../../../Utilities";
import { EmailAddressFormValues } from "../EmailAddress";
import { PhoneNumberFormValues } from "../PhoneNumber";
import { UserFormValues } from "../User";

export type Base = {
	givenName: Person["givenName"];
	middleName: Person["middleName"];
	familyName: Person["familyName"];
	nameOrder: Person["nameOrder"] | null;
	nickname: Person["nickname"];
	alternateNames: Person["alternateNames"];
	publicNameFormat: Person["publicNameFormat"] | null;
	imageKey: Person["imageKey"];
	imageContentType: Person["imageContentType"];
	gender: Person["gender"];
	dateOfBirth: Person["dateOfBirth"] | null;
	englishLanguageFluency: Person["englishLanguageFluency"] | null;
	occupation: Person["occupation"];
	nationalIdentificationNumber: Person["nationalIdentificationNumber"];
	governmentDocumentSeries: Person["governmentDocumentSeries"];
	governmentDocumentNumber: Person["governmentDocumentNumber"];
	governmentDocumentIssuedBy: Person["governmentDocumentIssuedBy"];
	addressIDs: Address["id"][];
	employeeID: Employee["id"] | null;
	guardianID: Guardian["id"] | null;
	languageIDs: Language["id"][];
	nationalityIDs: Country["id"][];
	studentID: Student["id"] | null;
};

export type Create = Base & {
	emailAddresses: EmailAddressFormValues.CreateInPerson[];
	phoneNumbers: PhoneNumberFormValues.CreateInPerson[];
	user: UserFormValues.CreateInPerson[];
};

export type CreateRequiredKey = Extract<keyof Create, "givenName" | "familyName">;

export type CreateSubset<K extends keyof Create = keyof Create> = Pick<Create, CreateRequiredKey | K>;

export const initialCreate: Create = {
	givenName: "",
	middleName: "",
	familyName: "",
	nameOrder: null,
	nickname: "",
	alternateNames: "",
	publicNameFormat: null,
	imageKey: "",
	imageContentType: "",
	gender: "",
	dateOfBirth: null,
	englishLanguageFluency: null,
	occupation: "",
	nationalIdentificationNumber: "",
	governmentDocumentSeries: "",
	governmentDocumentNumber: "",
	governmentDocumentIssuedBy: "",
	addressIDs: [],
	emailAddresses: [],
	employeeID: null,
	guardianID: null,
	languageIDs: [],
	nationalityIDs: [],
	phoneNumbers: [],
	studentID: null,
	user: [],
};

export type Detail = Base & {
	name: Person["name"];
	publicName: Person["publicName"];
	emailAddressIDs: EmailAddress["id"][];
	phoneNumberIDs: PhoneNumber["id"][];
	userID: User["id"] | null;
};

export type DetailSubset<K extends keyof Detail = keyof Detail> = Pick<Detail, K>;

export const englishLanguageFluencyOptions = [
	{ value: PersonEnglishLanguageFluency.A1, label: "A1" },
	{ value: PersonEnglishLanguageFluency.A2, label: "A2" },
	{ value: PersonEnglishLanguageFluency.B1, label: "B1" },
	{ value: PersonEnglishLanguageFluency.B2, label: "B2" },
	{ value: PersonEnglishLanguageFluency.C1, label: "C1" },
	{ value: PersonEnglishLanguageFluency.C2, label: "C2" },
	{ value: PersonEnglishLanguageFluency.NoKnowledge, label: "No Knowledge" },
];

export const nameOrderOptions = [{ value: PersonNameOrder.FamilyGiven }, { value: PersonNameOrder.GivenFamily }];

export const publicNameFormatOptions = [
	{ value: PersonPublicNameFormat.FullName },
	{ value: PersonPublicNameFormat.GivenNameFamilyInitial },
	{ value: PersonPublicNameFormat.GivenInitialFamilyName },
	{ value: PersonPublicNameFormat.GivenNameOnly },
	{ value: PersonPublicNameFormat.FamilyNameOnly },
	{ value: PersonPublicNameFormat.Hidden },
];

export type CreateInEmployee = Omit<Create, "employeeID">;

export type CreateInEmployeeRequiredKey = Exclude<CreateRequiredKey, "employeeID">;

export type CreateInEmployeeSubset<K extends keyof CreateInEmployee = keyof CreateInEmployee> = Pick<
	CreateInEmployee,
	CreateInEmployeeRequiredKey | K
>;

export const blankItemInEmployee: CreateInEmployee = omitKeys(initialCreate, ["employeeID"]);

export type CreateInGuardian = Omit<Create, "guardianID">;

export type CreateInGuardianRequiredKey = Exclude<CreateRequiredKey, "guardianID">;

export type CreateInGuardianSubset<K extends keyof CreateInGuardian = keyof CreateInGuardian> = Pick<
	CreateInGuardian,
	CreateInGuardianRequiredKey | K
>;

export const blankItemInGuardian: CreateInGuardian = {
	...omitKeys(initialCreate, ["guardianID"]),
	emailAddresses: [{ ...EmailAddressFormValues.blankItemInPerson, isPrimary: true }],
	phoneNumbers: [{ ...PhoneNumberFormValues.blankItemInPerson, isPrimary: true }],
};

export type CreateInStudent = Omit<Create, "studentID">;

export type CreateInStudentRequiredKey = Exclude<CreateRequiredKey, "studentID">;

export type CreateInStudentSubset<K extends keyof CreateInStudent = keyof CreateInStudent> = Pick<
	CreateInStudent,
	CreateInStudentRequiredKey | K
>;

export const blankItemInStudent: CreateInStudent = omitKeys(initialCreate, ["studentID"]);
